<template>
  <div class="flex flex-col">
    <FileInput
      :accept="validFileTypes.join(', ')"
      @input="setFiles"
    >
      <DocTypesHint />
    </FileInput>

    <InvalidFilesHint
      :files="invalidFiles"
      :error-messages="errorMessages"
    />

    <div
      v-if="validFiles.length > 0"
      class="flex flex-col gap-5 items-center w-full mt-3"
    >
      <DocumentList
        :files="validFiles"
        :file-names="fileNames"
        :disable-image-inputs="metaData.combine"
        :convert-to-pdf-flags="convertToPdfFlags"
        class="my-2.5"
        @set-file-name="setFileName"
        @set-convert-to-pdf="setConvertToPdf"
        @remove="remove"
      />

      <ElCheckbox
        v-if="hasMultipleImageFiles"
        id="combine-images-check"
        v-model="metaData.combine"
        :disabled="exceedsImageCombinationLimit"
        label="Bilder zu einer PDF-Datei kombinieren"
      />

      <ImageCombinationHint
        v-if="exceedsImageCombinationLimit"
        class="ml-1.5"
      />

      <MetaDataInput
        v-model:contract-id="metaData.contract_id"
        v-model:document-type-id="metaData.document_type_id"
        v-model:insurance-company-id="metaData.insurance_company_id"
        v-model:product-combo-id="metaData.product_combo_id"
        v-model:note="metaData.note"
        :contracts="contractOptions"
        :doc-types="documentTypeOptions"
        :companies="companyOptions"
        :sparten="sparten"
        :loading="contractsLoading"
      />

      <div
        v-if="hasError"
        class="my-2.5 text-danger text-center"
      >
        <template v-if="uploadErrorMessage">
          {{ uploadErrorMessage }}
        </template>
        <template v-else>
          Es gab einen Fehler beim Hochladen
          {{ validFiles.length > 1 ? 'der Dokumente' : 'des Dokuments' }}
        </template>
      </div>
    </div>

    <AppButton
      v-if="validFiles.length > 0"
      type="primary"
      class="w-full sm:w-auto mt-5 sticky bottom-0"
      :disabled="!validFiles || loading"
      :handler="uploadHandler"
    >
      Hochladen
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElCheckbox } from 'element-plus';
import { captureException } from '@sentry/vue';
import { ValidationError } from '@/api/lib/errors';
import { storeToRefs } from 'pinia';
import { reactive, ref, watch } from 'vue';

import { useGesellschaften } from '@/application/composables/gesellschaften';
import { useSparten } from '@/documents/composables/sparten';
import { toOptions } from '@/application/composables/vselect';
import { useContractStore } from '@/contracts/stores/contractStore';
import { useDocumentStore } from '@/documents/store/documentStore';
import { TrackEventCategory } from '@/application/utils/tracking/events';

import DocTypesHint from './DocTypesHint.vue';
import FileInput from './FileInput.vue';
import DocumentList from './FileList.vue';
import ImageCombinationHint from './ImageCombinationHint.vue';
import InvalidFilesHint from './InvalidFilesHint.vue';
import MetaDataInput from './MetaDataInput.vue';
import type { MetaData } from '../types/types';
import { useDocumentTypes } from "@/documents/composables/documentTypes";
import { isSupportedImage, useDocumentUpload } from "@/documents/composables/documentUpload";
import { useTagManager } from '@demv_systems/feu-tag-manager';

const emit = defineEmits(['files-set']);

const initMetaData: MetaData = {
  contract_id: null,
  insurance_company_id: null,
  product_combo_id: null,
  document_type_id: null,
  note: null,
  combine: false,
};

const metaData = reactive({ ...initMetaData });
const hasError = ref(false);
const uploadErrorMessage = ref<string | null>(null);
const loading = ref(false);

const { fetchDocuments } = useDocumentStore();
const { fetchContracts } = useContractStore();
const { contracts, contractsLoading, contractOptions } = storeToRefs(useContractStore());

const { gesellschaften, fetchGesellschaften } = useGesellschaften();
const { sparten, fetchSparten } = useSparten();

const {
  validFileTypes,
  validFiles,
  invalidFiles,
  fileNames,
  hasMultipleImageFiles,
  convertToPdfFlags,
  setConvertToPdf,
  exceedsImageCombinationLimit,
  errorMessages,
  setFiles,
  setFileName,
  upload,
  remove,
  reset
} = useDocumentUpload({ compressImages: true });
const { documentTypes, fetchDocumentTypes } = useDocumentTypes();

const documentTypeOptions = toOptions(documentTypes, 'name', 'id');
const companyOptions = toOptions(gesellschaften, 'name', 'id');

watch(() => validFiles.value, (valid) => {
  if (valid.length > 0) {
    emit('files-set', true);
  } else {
    emit('files-set', false);
  }
}, { deep: true });

watch(() => metaData.contract_id, (id) => {
  if (contracts.value.length <= 0) return;
  const contract = contracts.value.find((c) => c.id === id);
  metaData.insurance_company_id = contract?.company.id ?? null;
  metaData.product_combo_id = contract?.product.id ?? null;
});

watch(
  () => metaData.combine,
  () => validFiles.value.forEach((file, index) => {
    if (isSupportedImage(file)) {
      setFileName(file.name, index);
    }
  }),
);

watch([hasMultipleImageFiles, exceedsImageCombinationLimit], () => {
  metaData.combine = hasMultipleImageFiles.value && !exceedsImageCombinationLimit.value;
});

const clear = () => {
  invalidFiles.value = [];
  errorMessages.value = [];
  validFiles.value = [];
  hasError.value = false;
  uploadErrorMessage.value = null;
  reset();
  Object.assign(metaData, initMetaData);
};

const uploadFiles = async () => {
  if (!validFiles.value) return;
  hasError.value = false;
  uploadErrorMessage.value = null;
  loading.value = true;
  try {
    await upload(metaData);
    await fetchDocuments();
    clear();
    useTagManager().push({ event: TrackEventCategory.DOCUMENT_UPLOAD, action: 'documents' })
  } catch (error) {
    hasError.value = true;
    // 422-Antworten tragen eine nutzerverständliche Meldung vom Backend
    // (z.B. "Die Datei ... überschreitet die maximale Dateigröße von 20 MB.").
    uploadErrorMessage.value = error instanceof ValidationError ? error.message : null;
    captureException(error);
  } finally {
    loading.value = false;
  }
};

const uploadHandler = async () => {
  await uploadFiles();
};

fetchGesellschaften();
fetchSparten();
fetchContracts();
fetchDocumentTypes();
</script>
